import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ScrollArea } from "../../components/ui/scroll-area";
import { 
  MessageSquare, Plus, Hash, Send, Users, Settings, Bell, 
  UserPlus, X, Trash2, Building2, Lock, Globe, MoreVertical, Edit, Mail
} from "lucide-react";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function Channels() {
  const [channels, setChannels] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [channelDetailsOpen, setChannelDetailsOpen] = useState(false);
  const [editChannelOpen, setEditChannelOpen] = useState(false);
  const [startDMOpen, setStartDMOpen] = useState(false);
  const [directMessages, setDirectMessages] = useState([]);
  
  const [channelForm, setChannelForm] = useState({
    name: "", description: "", department_id: "none", is_private: false
  });
  const [editForm, setEditForm] = useState({ name: "", description: "", is_private: false });
  const [dmRecipient, setDmRecipient] = useState("");
  const [dmMessage, setDmMessage] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchDirectMessages();
  }, []);

  useEffect(() => {
    if (selectedChannel) {
      fetchMessages(selectedChannel.channel_id);
      // Poll for new messages every 5 seconds
      const interval = setInterval(() => {
        fetchMessages(selectedChannel.channel_id, true);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedChannel?.channel_id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchData = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const [channelsRes, deptsRes, teamRes] = await Promise.all([
        axios.get(`${API}/channels/channels`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/departments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/agent/team`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setChannels(channelsRes.data);
      setDepartments(deptsRes.data);
      setTeamMembers(teamRes.data.filter(t => t.status === "active"));
      
      // Auto-select first channel
      if (channelsRes.data.length > 0 && !selectedChannel) {
        setSelectedChannel(channelsRes.data[0]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDirectMessages = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const res = await axios.get(`${API}/channels/direct-messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDirectMessages(res.data);
    } catch (error) {
      console.error("Error fetching DMs:", error);
    }
  };

  const fetchMessages = async (channelId, silent = false) => {
    const token = localStorage.getItem("agent_token");
    try {
      const res = await axios.get(`${API}/channels/channels/${channelId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
      if (!silent) {
        // Update channel to clear unread
        setChannels(prev => prev.map(c => 
          c.channel_id === channelId ? { ...c, unread_count: 0 } : c
        ));
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const handleCreateChannel = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const res = await axios.post(`${API}/channels/channels`, {
        ...channelForm,
        department_id: channelForm.department_id === "none" ? null : channelForm.department_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Channel created");
      setCreateDialogOpen(false);
      setChannelForm({ name: "", description: "", department_id: "none", is_private: false });
      fetchData();
      setSelectedChannel(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create channel");
    }
  };

  const handleEditChannel = async () => {
    if (!selectedChannel) return;
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(`${API}/channels/channels/${selectedChannel.channel_id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Channel updated");
      setEditChannelOpen(false);
      fetchData();
      // Update selected channel
      setSelectedChannel(prev => ({ ...prev, ...editForm }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update channel");
    }
  };

  const openEditChannel = () => {
    if (selectedChannel) {
      setEditForm({
        name: selectedChannel.name || "",
        description: selectedChannel.description || "",
        is_private: selectedChannel.is_private || false
      });
      setEditChannelOpen(true);
    }
  };

  const handleStartDM = async () => {
    if (!dmRecipient || !dmMessage.trim()) {
      toast.error("Please select a recipient and enter a message");
      return;
    }
    const token = localStorage.getItem("agent_token");
    try {
      const res = await axios.post(`${API}/channels/direct-message`, {
        recipient_id: dmRecipient,
        content: dmMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Message sent");
      setStartDMOpen(false);
      setDmRecipient("");
      setDmMessage("");
      fetchDirectMessages();
      // Select the DM channel
      setSelectedChannel(res.data.channel);
      fetchMessages(res.data.channel.channel_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send message");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChannel || sendingMessage) return;
    
    setSendingMessage(true);
    const token = localStorage.getItem("agent_token");
    try {
      const res = await axios.post(`${API}/channels/channels/${selectedChannel.channel_id}/messages`, {
        content: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => [...prev, res.data]);
      setNewMessage("");
      messageInputRef.current?.focus();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleAddMembers = async () => {
    if (selectedMembers.length === 0) return;
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/channels/channels/${selectedChannel.channel_id}/members`, selectedMembers, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Added ${selectedMembers.length} members`);
      setAddMembersOpen(false);
      setSelectedMembers([]);
      fetchChannelDetails();
    } catch (error) {
      toast.error("Failed to add members");
    }
  };

  const handleRemoveMember = async (memberId) => {
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/channels/channels/${selectedChannel.channel_id}/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Member removed");
      fetchChannelDetails();
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  const handleDeleteChannel = async () => {
    if (!confirm("Are you sure you want to delete this channel? All messages will be lost.")) return;
    const token = localStorage.getItem("agent_token");
    try {
      await axios.delete(`${API}/channels/channels/${selectedChannel.channel_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Channel deleted");
      setSelectedChannel(null);
      setChannelDetailsOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete channel");
    }
  };

  const fetchChannelDetails = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const res = await axios.get(`${API}/channels/channels/${selectedChannel.channel_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedChannel(res.data);
    } catch (error) {
      console.error("Error fetching channel details:", error);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000 && date.getDate() === now.getDate()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 172800000) {
      return 'Yesterday ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + 
           date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const totalUnread = channels.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="h-[calc(100vh-180px)] flex gap-4">
        {/* Channel List */}
        <Card className="w-72 flex-shrink-0 flex flex-col">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Channels
                {totalUnread > 0 && (
                  <Badge variant="destructive" className="h-5 px-1.5 text-xs">{totalUnread}</Badge>
                )}
              </CardTitle>
              <Button size="sm" variant="ghost" onClick={() => setCreateDialogOpen(true)} data-testid="create-channel-btn">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full px-4 pb-4">
              <div className="space-y-1">
                {channels.map(channel => (
                  <button
                    key={channel.channel_id}
                    onClick={() => setSelectedChannel(channel)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                      selectedChannel?.channel_id === channel.channel_id 
                        ? 'bg-primary text-primary-foreground' 
                        : 'hover:bg-muted'
                    }`}
                    data-testid={`channel-${channel.channel_id}`}
                  >
                    {channel.is_private ? (
                      <Lock className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <Hash className="h-4 w-4 flex-shrink-0" />
                    )}
                    <span className="flex-1 truncate">{channel.name}</span>
                    {channel.unread_count > 0 && (
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">{channel.unread_count}</Badge>
                    )}
                  </button>
                ))}
                {channels.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No channels yet</p>
                    <Button size="sm" variant="link" onClick={() => setCreateDialogOpen(true)}>
                      Create one
                    </Button>
                  </div>
                )}

                {/* Direct Messages Section */}
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between mb-2 px-1">
                    <span className="text-xs font-semibold text-muted-foreground">Direct Messages</span>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setStartDMOpen(true)} data-testid="start-dm-btn">
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                  {directMessages.map(dm => (
                    <button
                      key={dm.channel_id}
                      onClick={() => { setSelectedChannel(dm); fetchMessages(dm.channel_id); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                        selectedChannel?.channel_id === dm.channel_id 
                          ? 'bg-primary text-primary-foreground' 
                          : 'hover:bg-muted'
                      }`}
                      data-testid={`dm-${dm.channel_id}`}
                    >
                      <Mail className="h-4 w-4 flex-shrink-0" />
                      <span className="flex-1 truncate">{dm.other_user?.full_name || "Unknown"}</span>
                      {dm.unread_count > 0 && (
                        <Badge variant="secondary" className="h-5 px-1.5 text-xs">{dm.unread_count}</Badge>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Messages Area */}
        <Card className="flex-1 flex flex-col">
          {selectedChannel ? (
            <>
              <CardHeader className="pb-3 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {selectedChannel.is_private ? <Lock className="h-5 w-5" /> : <Hash className="h-5 w-5" />}
                    <div>
                      <CardTitle className="text-lg">{selectedChannel.name}</CardTitle>
                      {selectedChannel.description && (
                        <CardDescription className="text-sm">{selectedChannel.description}</CardDescription>
                      )}
                    </div>
                    {selectedChannel.department && (
                      <Badge variant="outline" className="ml-2">
                        <Building2 className="h-3 w-3 mr-1" />
                        {selectedChannel.department.name}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={() => { fetchChannelDetails(); setChannelDetailsOpen(true); }}>
                      <Users className="h-4 w-4 mr-1" />
                      {selectedChannel.members?.length || 0}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { fetchChannelDetails(); setAddMembersOpen(true); }}>
                      <UserPlus className="h-4 w-4" />
                    </Button>
                    {!selectedChannel.is_dm && (
                      <Button size="sm" variant="ghost" onClick={openEditChannel} data-testid="edit-channel-btn">
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="flex-1 overflow-hidden p-0">
                <ScrollArea className="h-full p-4">
                  <div className="space-y-4">
                    {messages.map((msg, idx) => {
                      const showAvatar = idx === 0 || messages[idx - 1]?.sender_id !== msg.sender_id;
                      return (
                        <div key={msg.message_id} className="flex gap-3">
                          {showAvatar ? (
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm font-semibold flex-shrink-0">
                              {msg.sender?.full_name?.[0] || "?"}
                            </div>
                          ) : (
                            <div className="w-8" />
                          )}
                          <div className="flex-1 min-w-0">
                            {showAvatar && (
                              <div className="flex items-baseline gap-2 mb-1">
                                <span className="font-semibold text-sm">{msg.sender?.full_name || "Unknown"}</span>
                                <span className="text-xs text-muted-foreground">{formatTime(msg.created_at)}</span>
                              </div>
                            )}
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                          </div>
                        </div>
                      );
                    })}
                    {messages.length === 0 && (
                      <div className="text-center py-12 text-muted-foreground">
                        <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No messages yet</p>
                        <p className="text-sm">Be the first to say hello!</p>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>
              
              <div className="p-4 border-t flex-shrink-0">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    ref={messageInputRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={`Message #${selectedChannel.name}`}
                    className="flex-1"
                    disabled={sendingMessage}
                    data-testid="message-input"
                  />
                  <Button type="submit" disabled={!newMessage.trim() || sendingMessage} data-testid="send-message-btn">
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <CardContent className="flex-1 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Select a channel to start chatting</p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Create Channel Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Channel</DialogTitle>
            <DialogDescription>Create a new communication channel for your team</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Channel Name *</Label>
              <Input 
                value={channelForm.name} 
                onChange={(e) => setChannelForm(p => ({ ...p, name: e.target.value }))} 
                placeholder="e.g., general, sales-team"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={channelForm.description} 
                onChange={(e) => setChannelForm(p => ({ ...p, description: e.target.value }))} 
                placeholder="What's this channel about?"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Link to Department (auto-adds members)</Label>
              <Select value={channelForm.department_id} onValueChange={(v) => setChannelForm(p => ({ ...p, department_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No department</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept.department_id} value={dept.department_id}>
                      {dept.name} ({dept.employee_count || 0} members)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="is_private"
                checked={channelForm.is_private} 
                onChange={(e) => setChannelForm(p => ({ ...p, is_private: e.target.checked }))}
                className="h-4 w-4"
              />
              <Label htmlFor="is_private" className="text-sm cursor-pointer flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Make this channel private
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateChannel} disabled={!channelForm.name.trim()}>Create Channel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Members Dialog */}
      <Dialog open={addMembersOpen} onOpenChange={setAddMembersOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Members</DialogTitle>
            <DialogDescription>Add team members to {selectedChannel?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <ScrollArea className="h-[300px] border rounded-lg p-2">
              {teamMembers.filter(t => !selectedChannel?.members?.includes(t.agent_id)).map(member => (
                <label key={member.agent_id} className="flex items-center gap-3 p-2 hover:bg-muted rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(member.agent_id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedMembers(p => [...p, member.agent_id]);
                      } else {
                        setSelectedMembers(p => p.filter(id => id !== member.agent_id));
                      }
                    }}
                    className="h-4 w-4"
                  />
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm">
                    {member.full_name?.[0]}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{member.full_name}</p>
                    <p className="text-xs text-muted-foreground">{member.email}</p>
                  </div>
                </label>
              ))}
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddMembersOpen(false); setSelectedMembers([]); }}>Cancel</Button>
            <Button onClick={handleAddMembers} disabled={selectedMembers.length === 0}>
              Add {selectedMembers.length} Member{selectedMembers.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Channel Details Dialog */}
      <Dialog open={channelDetailsOpen} onOpenChange={setChannelDetailsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedChannel?.is_private ? <Lock className="h-5 w-5" /> : <Hash className="h-5 w-5" />}
              {selectedChannel?.name}
            </DialogTitle>
            {selectedChannel?.description && (
              <DialogDescription>{selectedChannel.description}</DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground text-sm">Members ({selectedChannel?.member_details?.length || 0})</Label>
              <ScrollArea className="h-[200px] mt-2 border rounded-lg">
                <div className="p-2 space-y-1">
                  {selectedChannel?.member_details?.map(member => (
                    <div key={member.agent_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-sm">
                          {member.full_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{member.full_name}</p>
                          <div className="flex items-center gap-1">
                            <span className={`w-2 h-2 rounded-full ${member.status === 'online' ? 'bg-green-500' : 'bg-gray-400'}`} />
                            <span className="text-xs text-muted-foreground capitalize">{member.status || 'offline'}</span>
                          </div>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleRemoveMember(member.agent_id)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="destructive" onClick={handleDeleteChannel} className="w-full sm:w-auto">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Channel
            </Button>
            <Button variant="outline" onClick={() => setChannelDetailsOpen(false)} className="w-full sm:w-auto">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Channel Dialog */}
      <Dialog open={editChannelOpen} onOpenChange={setEditChannelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Channel</DialogTitle>
            <DialogDescription>Update channel settings</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Channel Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., general, marketing"
                data-testid="edit-channel-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm(p => ({ ...p, description: e.target.value }))}
                placeholder="What's this channel about?"
                data-testid="edit-channel-description"
              />
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="edit_is_private"
                checked={editForm.is_private} 
                onChange={(e) => setEditForm(p => ({ ...p, is_private: e.target.checked }))}
                className="h-4 w-4"
              />
              <Label htmlFor="edit_is_private" className="text-sm cursor-pointer flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Make this channel private
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditChannelOpen(false)}>Cancel</Button>
            <Button onClick={handleEditChannel} disabled={!editForm.name.trim()} data-testid="save-channel-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start Direct Message Dialog */}
      <Dialog open={startDMOpen} onOpenChange={setStartDMOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start a Conversation</DialogTitle>
            <DialogDescription>Send a direct message to a team member</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Team Member</Label>
              <Select value={dmRecipient || "none"} onValueChange={(v) => setDmRecipient(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="dm-recipient-select">
                  <SelectValue placeholder="Choose a recipient" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select a team member</SelectItem>
                  {teamMembers.map(member => (
                    <SelectItem key={member.agent_id} value={member.agent_id}>
                      {member.full_name} ({member.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Input
                value={dmMessage}
                onChange={(e) => setDmMessage(e.target.value)}
                placeholder="Type your message..."
                data-testid="dm-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setStartDMOpen(false); setDmRecipient(""); setDmMessage(""); }}>Cancel</Button>
            <Button onClick={handleStartDM} disabled={!dmRecipient || !dmMessage.trim()} data-testid="send-dm-btn">
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AgentLayout>
  );
}
